import { useEffect, useRef, useState } from 'react'
import {
  addAuthorVisibleMs,
  appendPoseHold,
  appendPoseWaypoint,
  authorTimePlaceholder,
  displayToMeters,
  distanceUnitLabel,
  distanceUnitShort,
  formatAuthorTime,
  formatPosePaste,
  metersToDisplay,
  parseAuthorTime,
  parsePosePaste,
  poseLocationRuns,
  posesSameSpot,
  retimePosePathWithIntent,
  uniquePoseAt,
  WAYPOINT_STEP_MS,
  POSE_CSV_HEADER,
  type PoseRetimeIntent,
  type ScenarioClockConfig,
} from './format'
import type {
  DisplayUnits,
  NumberKeyframe,
  PoseKeyframe,
  RoadStatus,
  StatusKeyframe,
  VisibilityKeyframe,
} from './types'
import {
  sortNumberFrames,
  sortPoseFrames,
  sortStatusFrames,
  sortVisFrames,
} from './keyframeSort'
import { addStoryMs } from './storyTime'

export function ElapsedInput({
  valueIso,
  allowEmpty,
  disabled,
  hint,
  flash,
  clock,
  onCommit,
  onFocusRow,
  onBlurRow,
}: {
  valueIso: string | null
  allowEmpty?: boolean
  disabled?: boolean
  hint?: boolean
  /** Brief border flash after a successful commit (parent-controlled). */
  flash?: boolean
  clock: ScenarioClockConfig
  onCommit: (iso: string | null) => void
  /** Sticky row selection when the time field receives focus. */
  onFocusRow?: () => void
  /** Always called on blur (even when the value is unchanged). */
  onBlurRow?: () => void
}) {
  const [text, setText] = useState(valueIso == null ? '' : formatAuthorTime(valueIso, clock))
  const [bad, setBad] = useState(false)
  const focusedRef = useRef(false)
  const focusValueRef = useRef(valueIso)
  /** Text at focus — blur must not re-commit unchanged text. */
  const focusTextRef = useRef(valueIso == null ? '' : formatAuthorTime(valueIso, clock))

  useEffect(() => {
    // Don't clobber in-progress typing when parent clock/session identity churns.
    if (focusedRef.current) return
    setText(valueIso == null ? '' : formatAuthorTime(valueIso, clock))
    setBad(false)
  }, [valueIso, clock.startsAt, clock.timezone, clock.durationMs, clock.timeFormat])

  function commit(raw: string): void {
    const trimmed = raw.trim()
    if (!trimmed) {
      if (allowEmpty) {
        setBad(false)
        if (focusValueRef.current !== null) onCommit(null)
        return
      }
      setBad(true)
      return
    }
    const iso = parseAuthorTime(trimmed, clock)
    if (iso == null) {
      setBad(true)
      return
    }
    setBad(false)
    setText(formatAuthorTime(iso, clock))
    if (iso !== focusValueRef.current) onCommit(iso)
  }

  return (
    <div className="sc-elapsed-wrap">
      <div className="sc-elapsed-field">
        <input
          className={`sc-fin${bad ? ' bad' : ''}${flash ? ' sc-field-flash' : ''}`}
          value={text}
          placeholder={authorTimePlaceholder(clock)}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={bad}
          onFocus={() => {
            focusedRef.current = true
            focusValueRef.current = valueIso
            focusTextRef.current = text
            onFocusRow?.()
          }}
          onChange={(e) => {
            const next = e.target.value
            setText(next)
            if (!bad) return
            const trimmed = next.trim()
            setBad(trimmed ? parseAuthorTime(trimmed, clock) == null : !allowEmpty)
          }}
          onBlur={(e) => {
            focusedRef.current = false
            const raw = e.target.value
            // Unchanged text: keep stored ms. Re-parsing can nudge ms when format≠parse identity.
            if (raw.trim() === focusTextRef.current.trim()) {
              setText(valueIso == null ? '' : formatAuthorTime(valueIso, clock))
              setBad(false)
              onBlurRow?.()
              return
            }
            commit(raw)
            onBlurRow?.()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          }}
        />
        {allowEmpty && valueIso != null && !disabled ? (
          <button
            type="button"
            className="sc-elapsed-clear"
            title="Clear time"
            aria-label="Clear time"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setBad(false)
              setText('')
              if (valueIso != null) onCommit(null)
            }}
          >
            ×
          </button>
        ) : null}
      </div>
      {hint && bad ? <span className="sc-elapsed-err">Check time format</span> : null}
    </div>
  )
}

export function NumberSeriesEditor({
  frames,
  displayUnits,
  editing,
  clock,
  appearAt,
  playheadIso,
  focusIndex = null,
  previewIndex,
  numberPreviewActive = false,
  onToggleNumberPreview,
  onFocusIndex,
  onChange,
  defaultValue = 0,
}: {
  frames: NumberKeyframe[]
  displayUnits: DisplayUnits
  editing: boolean
  clock: ScenarioClockConfig
  appearAt?: string | null
  playheadIso: string
  focusIndex?: number | null
  previewIndex?: number | null
  numberPreviewActive?: boolean
  onToggleNumberPreview?: () => void
  onFocusIndex?: (index: number | null) => void
  onChange: (frames: NumberKeyframe[] | undefined) => void
  /** Seed value when adding the first row (e.g. current radiusM). */
  defaultValue?: number
}) {
  const unit = distanceUnitLabel(displayUnits)
  const unitShort = distanceUnitShort(displayUnits)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    if (previewIndex == null) return
    const row = rowRefs.current[previewIndex]
    const body = row?.closest('.sc-fold-body')
    if (body && !body.classList.contains('open')) return
    row?.scrollIntoView({ block: 'nearest' })
  }, [previewIndex])

  function firstAt(): string {
    return appearAt ?? playheadIso
  }

  const showTableChrome = editing || frames.length > 0

  return (
    <div className="sc-series">
      {showTableChrome ? (
        <div className={`sc-series-head sc-series-head-nums${editing ? '' : ' read'}`}>
          <span>Time</span>
          <span>{unit}</span>
          {editing ? <span className="sc-series-head-x" aria-hidden="true" /> : null}
        </div>
      ) : null}
      {frames.map((frame, index) => {
        const rowTone =
          previewIndex != null
            ? previewIndex === index
              ? ' preview'
              : ''
            : focusIndex === index
              ? ' hot'
              : ''
        return (
          <div
            key={`${frame.at}-${index}`}
            ref={(el) => {
              rowRefs.current[index] = el
            }}
            className={`sc-series-row nums${editing ? '' : ' read'}${rowTone}`}
            onMouseEnter={() => {
              // Edit: sticky click focus only (hover must not seek / start Preview).
              if (editing || previewIndex != null || !onFocusIndex) return
              onFocusIndex(index)
            }}
            onMouseLeave={() => {
              if (editing || previewIndex != null || !onFocusIndex) return
              onFocusIndex(null)
            }}
            onClick={() => {
              if (previewIndex != null || !onFocusIndex) return
              if (editing) onFocusIndex(index)
            }}
          >
            {editing ? (
              <>
                <ElapsedInput
                  valueIso={frame.at}
                  clock={clock}
                  onFocusRow={() => {
                    if (previewIndex != null || !onFocusIndex) return
                    onFocusIndex(index)
                  }}
                  onCommit={(ms) => {
                    if (ms == null) return
                    onChange(
                      sortNumberFrames(
                        frames.map((f, i) => (i === index ? { ...f, at: ms } : f)),
                      ),
                    )
                  }}
                />
                <div className="sc-unit-field">
                  <input
                    className="sc-fin"
                    type="number"
                    step="1"
                    value={metersToDisplay(frame.value, displayUnits)}
                    aria-label={unit}
                    onChange={(e) => {
                      const displayed = Number(e.target.value)
                      if (!Number.isFinite(displayed)) return
                      const value = displayToMeters(displayed, displayUnits)
                      onChange(frames.map((f, i) => (i === index ? { ...f, value } : f)))
                    }}
                  />
                  <span className="sc-unit-affix" aria-hidden="true">
                    {unitShort}
                  </span>
                </div>
                <button
                  type="button"
                  className="sc-x"
                  aria-label="Remove"
                  onClick={(e) => {
                    e.stopPropagation()
                    const next = frames.filter((_, i) => i !== index)
                    onChange(next.length ? sortNumberFrames(next) : undefined)
                  }}
                >
                  ×
                </button>
              </>
            ) : (
              <>
                <span className="time">{formatAuthorTime(frame.at, clock)}</span>
                <span className="val">
                  {metersToDisplay(frame.value, displayUnits)} {unit}
                </span>
              </>
            )}
          </div>
        )
      })}
      {editing ? (
        <div className="sc-series-actions">
          <button
            type="button"
            className="sc-btn"
            onClick={() => {
              const last = frames[frames.length - 1]
              onChange(
                sortNumberFrames([
                  ...frames,
                  {
                    at: last ? addStoryMs(last.at, 5_000) : firstAt(),
                    value: last?.value ?? defaultValue,
                  },
                ]),
              )
            }}
          >
            Add
          </button>
          {frames.length ? (
            <button type="button" className="sc-btn" onClick={() => onChange(undefined)}>
              Clear all
            </button>
          ) : null}
          {frames.length >= 2 && onToggleNumberPreview ? (
            <button
              type="button"
              className={`sc-btn${numberPreviewActive ? ' on' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={onToggleNumberPreview}
            >
              {numberPreviewActive ? 'Stop' : 'Preview'}
            </button>
          ) : null}
        </div>
      ) : frames.length >= 2 && onToggleNumberPreview ? (
        <div className="sc-series-actions">
          <button
            type="button"
            className={`sc-btn${numberPreviewActive ? ' on' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onToggleNumberPreview}
          >
            {numberPreviewActive ? 'Stop' : 'Preview'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function StatusSeriesEditor({
  frames,
  editing,
  clock,
  appearAt,
  playheadIso,
  defaultStatus = 'open',
  focusIndex = null,
  onFocusIndex,
  onChange,
}: {
  frames: StatusKeyframe[]
  editing: boolean
  clock: ScenarioClockConfig
  appearAt?: string | null
  playheadIso: string
  /** Seed status when adding the first row (from params.status). */
  defaultStatus?: RoadStatus
  focusIndex?: number | null
  onFocusIndex?: (index: number | null) => void
  onChange: (frames: StatusKeyframe[] | undefined) => void
}) {
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])
  const [flashMs, setFlashMs] = useState<string | null>(null)
  const flashTimerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (flashTimerRef.current != null) window.clearTimeout(flashTimerRef.current)
    }
  }, [])

  function firstAt(): string {
    return appearAt ?? playheadIso
  }

  function flashTime(ms: string): void {
    if (flashTimerRef.current != null) window.clearTimeout(flashTimerRef.current)
    setFlashMs(ms)
    flashTimerRef.current = window.setTimeout(() => {
      setFlashMs(null)
      flashTimerRef.current = null
    }, 650)
  }

  function commitTime(index: number, ms: string): void {
    const unique = uniquePoseAt(ms, frames, index)
    const cur = frames[index]
    if (!cur) return
    const updated = frames.map((f, i) => (i === index ? { ...f, at: unique } : f))
    onChange(sortStatusFrames(updated))
    flashTime(unique)
  }

  function nextStatus(prev: RoadStatus | undefined): RoadStatus {
    if (prev === 'open') return 'slow'
    if (prev === 'slow') return 'closed'
    return 'open'
  }

  function statusLabel(status: RoadStatus): string {
    return status === 'open' ? 'Open' : status === 'slow' ? 'Slow' : 'Closed'
  }

  const showTableChrome = editing || frames.length > 0

  return (
    <div className="sc-series">
      {showTableChrome ? (
        <div className={`sc-series-head sc-series-head-vis-time${editing ? '' : ' read'}`}>
          <span className="sc-series-head-num">#</span>
          <span>Time</span>
          <span>Status</span>
          {editing ? <span className="sc-series-head-x" aria-hidden="true" /> : null}
        </div>
      ) : null}
      {frames.length
        ? frames.map((frame, index) => {
            const rowTone = editing && focusIndex === index ? ' hot' : ''
            return (
              <div
                key={`${frame.at}-${index}`}
                ref={(el) => {
                  rowRefs.current[index] = el
                }}
                className={`sc-series-row vis-time${editing ? '' : ' read'}${rowTone}`}
                onClick={() => {
                  if (!editing || !onFocusIndex) return
                  onFocusIndex(index)
                }}
              >
                <span className="sc-series-num" aria-hidden="true">
                  {index + 1}
                </span>
                {editing ? (
                  <>
                    <ElapsedInput
                      valueIso={frame.at}
                      clock={clock}
                      flash={flashMs === frame.at}
                      onFocusRow={() => {
                        if (!onFocusIndex) return
                        onFocusIndex(index)
                      }}
                      onCommit={(ms) => {
                        if (ms == null) return
                        commitTime(index, ms)
                      }}
                    />
                    <select
                      className="sc-fin sc-perm"
                      value={frame.status}
                      aria-label="Status"
                      onChange={(e) => {
                        const status = e.target.value as RoadStatus
                        onChange(frames.map((f, i) => (i === index ? { ...f, status } : f)))
                      }}
                    >
                      <option value="open">Open</option>
                      <option value="slow">Slow</option>
                      <option value="closed">Closed</option>
                    </select>
                    <button
                      type="button"
                      className="sc-x"
                      title="Remove"
                      aria-label="Remove"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        const next = frames.filter((_, i) => i !== index)
                        onChange(next.length ? sortStatusFrames(next) : undefined)
                      }}
                    >
                      ×
                    </button>
                  </>
                ) : (
                  <>
                    <span className="val sc-series-time-read">
                      {formatAuthorTime(frame.at, clock)}
                    </span>
                    <span className="val">{statusLabel(frame.status)}</span>
                  </>
                )}
              </div>
            )
          })
        : editing ? (
          <button
            type="button"
            className="sc-series-empty"
            title="Add the first status step at Show time or the current clock"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() =>
              onChange([
                {
                  at: firstAt(),
                  status: defaultStatus,
                },
              ])
            }
          >
            Click Add to set first status time
          </button>
        ) : null}
      {editing ? (
        <div className="sc-series-actions">
          <button
            type="button"
            className="sc-btn"
            title={
              frames.length
                ? `Add the next step (+${WAYPOINT_STEP_MS / 1000}s), cycling Open → Slow → Closed`
                : 'Add the first status step'
            }
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (!frames.length) {
                onChange([{ at: firstAt(), status: defaultStatus }])
                return
              }
              const sorted = sortStatusFrames(frames)
              const last = sorted[sorted.length - 1]!
              const at = uniquePoseAt(
                addAuthorVisibleMs(last.at, WAYPOINT_STEP_MS, clock),
                sorted,
              )
              onChange(
                sortStatusFrames([
                  ...sorted,
                  { at, status: nextStatus(last.status) },
                ]),
              )
            }}
          >
            Add
          </button>
          <button
            type="button"
            className="sc-btn"
            disabled={!frames.length}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange(undefined)}
          >
            Clear all
          </button>
        </div>
      ) : null}
    </div>
  )
}
export function CoordInput({
  value,
  ariaLabel,
  flash,
  disabled,
  onCommit,
  onFocus,
}: {
  value: number
  ariaLabel: string
  flash?: boolean
  disabled?: boolean
  onCommit: (n: number) => void
  onFocus?: () => void
}) {
  const [text, setText] = useState(() => value.toFixed(7))
  useEffect(() => {
    setText(value.toFixed(7))
  }, [value])
  return (
    <input
      className={`sc-fin sc-series-coord${flash ? ' flash' : ''}`}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      spellCheck={false}
      value={text}
      disabled={disabled}
      aria-label={ariaLabel}
      placeholder="—"
      onChange={(e) => setText(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onFocus={() => onFocus?.()}
      onBlur={() => {
        const trimmed = text.trim()
        // Empty / wipe: restore prior value — Number('') is 0 and would slam the pin to null island.
        if (!trimmed) {
          setText(value.toFixed(7))
          return
        }
        const n = Number(trimmed)
        if (!Number.isFinite(n)) {
          setText(value.toFixed(7))
          return
        }
        setText(n.toFixed(7))
        // Skip no-op commits — blur-on-unmount after Clear must not rewrite keyframes.
        if (n === value) return
        onCommit(n)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
    />
  )
}

export function PoseSeriesEditor({
  frames,
  editing,
  focusIndex,
  flashIndex,
  previewIndex,
  previewHolding,
  pathPreviewActive = false,
  onTogglePathPreview,
  showRetime = true,
  anchorPose,
  appearAt,
  playheadIso,
  clock,
  onFocusIndex,
  onFlashIndex,
  onChange,
}: {
  frames: PoseKeyframe[]
  editing: boolean
  focusIndex: number | null
  flashIndex: number | null
  previewIndex?: number | null
  previewHolding?: boolean
  pathPreviewActive?: boolean
  onTogglePathPreview?: () => void
  /** Vehicle-oriented travel retiming — hide for tornado tracks. */
  showRetime?: boolean
  /** Used when Add is clicked with an empty list. */
  anchorPose?: { lng: number; lat: number } | null
  appearAt?: string | null
  /** Strip / playback elapsed — seeds pin 1 when Show time is empty. */
  playheadIso: string
  clock: ScenarioClockConfig
  onFocusIndex: (index: number | null) => void
  /** Green flash after a time-field commit (not Add / Add hold). */
  onFlashIndex?: (index: number) => void
  onChange: (frames: PoseKeyframe[] | undefined) => void
}) {
  const [pasteBad, setPasteBad] = useState(false)
  const [ioOpen, setIoOpen] = useState(false)
  const [ioTab, setIoTab] = useState<'import' | 'export'>('import')
  const [csvDraft, setCsvDraft] = useState(() => formatPosePaste(frames, clock))
  const [ioNote, setIoNote] = useState<string | null>(null)
  const ioNoteTimerRef = useRef<number | null>(null)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])
  const [retimeOpen, setRetimeOpen] = useState(false)
  const [retimeIntent, setRetimeIntent] = useState<PoseRetimeIntent>('leave-at-start')
  const [retimeEndText, setRetimeEndText] = useState('')
  const [retimeBad, setRetimeBad] = useState(false)

  const exportText = formatPosePaste(frames, clock)
  const csvHint = 'Format: HH:MM:SS, lat, lng'
  const locationRuns = poseLocationRuns(frames)
  const locMeta = frames.map(() => ({
    label: 0,
    dwell: false,
    zebra: false,
    runStart: 0,
  }))
  for (const run of locationRuns) {
    const zebra = run.label % 2 === 0
    const dwell = run.endIndex > run.startIndex
    for (let i = run.startIndex; i <= run.endIndex; i++) {
      locMeta[i] = { label: run.label, dwell, zebra, runStart: run.startIndex }
    }
  }

  function addHoldAt(index: number): void {
    const anchor = frames[index]
    const next = appendPoseHold(frames, index, clock)
    onChange(next)
    if (!anchor) {
      onFocusIndex(next.length - 1)
      return
    }
    const run = poseLocationRuns(next).find((r) =>
      posesSameSpot({ lng: r.lng, lat: r.lat }, anchor),
    )
    onFocusIndex(run ? run.endIndex : next.length - 1)
  }

  function flashIoNote(message: string): void {
    setIoNote(message)
    if (ioNoteTimerRef.current != null) window.clearTimeout(ioNoteTimerRef.current)
    ioNoteTimerRef.current = window.setTimeout(() => {
      setIoNote(null)
      ioNoteTimerRef.current = null
    }, 1600)
  }

  function seedImportDraft(): void {
    setCsvDraft(formatPosePaste(frames, clock))
    setPasteBad(false)
  }

  function seedRetimeDraft(): void {
    const last = frames[frames.length - 1]
    setRetimeEndText(last ? formatAuthorTime(last.at, clock) : '')
    setRetimeBad(false)
  }

  function applyRetime(): void {
    if (frames.length < 2) return
    let endIso: string | undefined
    if (retimeIntent === 'arrive-at-end') {
      const parsed = parseAuthorTime(retimeEndText.trim(), clock)
      if (parsed == null) {
        setRetimeBad(true)
        return
      }
      endIso = parsed
    } else if (retimeIntent === 'leave-at-start') {
      endIso = frames[frames.length - 1]!.at
    }
    setRetimeBad(false)
    const next = retimePosePathWithIntent(frames, retimeIntent, endIso)
    onChange(next)
    onFocusIndex(Math.max(0, next.length - 1))
    setRetimeOpen(false)
  }

  function firstPinAt(): string {
    return appearAt ?? playheadIso
  }

  function commitArriveTime(index: number, ms: string): void {
    const unique = uniquePoseAt(ms, frames, index)
    const cur = frames[index]
    if (!cur) return
    if (unique === cur.at) return
    const updated = frames.map((f, i) => (i === index ? { ...f, at: unique } : f))
    const sorted = sortPoseFrames(updated)
    const newIndex = sorted.findIndex(
      (f) => f.at === unique && f.lng === cur.lng && f.lat === cur.lat,
    )
    onChange(sorted)
    const focusAt = newIndex >= 0 ? newIndex : null
    onFocusIndex(focusAt)
    if (focusAt != null) onFlashIndex?.(focusAt)
  }

  useEffect(() => {
    return () => {
      if (ioNoteTimerRef.current != null) window.clearTimeout(ioNoteTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (focusIndex == null) return
    const row = rowRefs.current[focusIndex]
    const body = row?.closest('.sc-fold-body')
    if (body && !body.classList.contains('open')) return
    row?.scrollIntoView({ block: 'nearest' })
  }, [focusIndex])

  useEffect(() => {
    if (previewIndex == null) return
    const row = rowRefs.current[previewIndex]
    const body = row?.closest('.sc-fold-body')
    if (body && !body.classList.contains('open')) return
    row?.scrollIntoView({ block: 'nearest' })
  }, [previewIndex])

  function applyImport(): void {
    const next = parsePosePaste(csvDraft, frames[0]?.at ?? playheadIso, clock)
    if (!next?.length) {
      setPasteBad(true)
      return
    }
    setPasteBad(false)
    const ok = window.confirm(
      `Replace all ${frames.length} waypoint${frames.length === 1 ? '' : 's'} in the table with ${next.length} from CSV?`,
    )
    if (!ok) return
    onChange(sortPoseFrames(next))
    setCsvDraft(formatPosePaste(next, clock))
    onFocusIndex(null)
    flashIoNote('Imported')
  }

  const showTableChrome = editing || frames.length > 0

  return (
    <div className="sc-series">
      {showTableChrome ? (
        <div className={`sc-series-head sc-series-head-pose${editing ? '' : ' read'}`}>
          <span
            className="sc-series-head-num"
            title="Map pin number — same number means the same place (a hold is another time at that pin)"
          >
            #
          </span>
          <span title="Session time at this pin — same # with a later time is a hold">Time</span>
          <span className="sc-series-head-hold" title="Same place, later time">
            Hold
          </span>
          {editing ? <span className="sc-series-head-x" aria-hidden="true" /> : null}
        </div>
      ) : null}
      {frames.length
        ? frames.map((frame, index) => {
            // During path preview only one row lights — never mix with hover/focus `hot`.
            // Edit: sticky click focus (no hover steal). Read-only: hover previews the map pin.
            const loc = locMeta[index] ?? {
              label: index + 1,
              dwell: false,
              zebra: false,
              runStart: index,
            }
            const rowTone =
              previewIndex != null
                ? previewIndex === index
                  ? previewHolding
                    ? ' preview-hold'
                    : ' preview'
                  : ''
                : focusIndex === index
                  ? ' hot'
                  : ''
            const groupTone = rowTone
              ? ''
              : `${loc.zebra ? ' loc-b' : ' loc-a'}${loc.dwell ? ' dwell' : ''}`
            const isWaitRow = loc.dwell && index > loc.runStart
            const pinTitle = !loc.dwell
              ? `Pin ${loc.label}`
              : index === loc.runStart
                ? `Pin ${loc.label} — arrive / start of hold`
                : `Pin ${loc.label} — hold (wait here until this time)`
            return (
              <div
                key={`${frame.at}-${frame.lng}-${frame.lat}-${index}`}
                ref={(el) => {
                  rowRefs.current[index] = el
                }}
                className={`sc-series-row pose${editing ? '' : ' read'}${rowTone}${groupTone}${isWaitRow ? ' wait' : ''}`}
                onMouseEnter={() => {
                  if (editing || previewIndex != null) return
                  onFocusIndex(index)
                }}
                onMouseLeave={() => {
                  if (editing || previewIndex != null) return
                  onFocusIndex(null)
                }}
                onClick={() => {
                  if (previewIndex != null) return
                  if (editing) onFocusIndex(index)
                }}
              >
                <span
                  className={`sc-series-num${loc.dwell ? ' dwell' : ''}${isWaitRow ? ' wait' : ''}`}
                  title={pinTitle}
                  aria-hidden="true"
                >
                  {loc.label}
                </span>
                {editing ? (
                  <>
                    <ElapsedInput
                      valueIso={frame.at}
                      clock={clock}
                      flash={flashIndex === index}
                      onFocusRow={() => {
                        if (previewIndex != null) return
                        onFocusIndex(index)
                      }}
                      onCommit={(ms) => {
                        if (ms == null) return
                        commitArriveTime(index, ms)
                      }}
                    />
                    {!loc.dwell ? (
                      <button
                        type="button"
                        className="sc-series-hold"
                        title={`Add a hold at the selected pin (+${WAYPOINT_STEP_MS / 1000}s) — same map pin, new time row`}
                        aria-label={`Add hold at pin ${loc.label}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.stopPropagation()
                          onFocusIndex(index)
                          addHoldAt(index)
                        }}
                      >
                        Add hold
                      </button>
                    ) : isWaitRow ? (
                      <span className="sc-series-hold-slot sc-series-wait-tag" title={pinTitle}>
                        Hold
                      </span>
                    ) : (
                      <span className="sc-series-hold-slot" aria-hidden="true" />
                    )}
                    <button
                      type="button"
                      className="sc-x"
                      title="Remove"
                      aria-label="Remove"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation()
                        const next = frames.filter((_, i) => i !== index)
                        onChange(next.length ? sortPoseFrames(next) : undefined)
                        onFocusIndex(null)
                      }}
                    >
                      ×
                    </button>
                  </>
                ) : (
                  <>
                    <span className="val sc-series-time-read">
                      {formatAuthorTime(frame.at, clock)}
                    </span>
                    {isWaitRow ? (
                      <span className="sc-series-wait-tag" title={pinTitle}>
                        Hold
                      </span>
                    ) : (
                      <span className="sc-series-hold-slot" aria-hidden="true" />
                    )}
                  </>
                )}
              </div>
            )
          })
        : editing ? (
          <button
            type="button"
            className="sc-series-empty"
            disabled={!anchorPose}
            title={
              anchorPose
                ? 'Place pin 1 at this object’s map position'
                : 'Object needs a map position first'
            }
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (!anchorPose) return
              onChange([
                {
                  at: firstPinAt(),
                  lng: anchorPose.lng,
                  lat: anchorPose.lat,
                },
              ])
              onFocusIndex(0)
            }}
          >
            Click Add to place first waypoint
          </button>
        ) : (
          <p className="sc-hint">No waypoints on this object.</p>
        )}
      {editing ? (
        <>
          <div className="sc-series-actions">
            <button
              type="button"
              className="sc-btn"
              title={
                frames.length
                  ? `Add the next pin near the last one (+${WAYPOINT_STEP_MS / 1000}s) — drag it on the map`
                  : 'Place pin 1 at this object’s map position'
              }
              disabled={!frames.length && !anchorPose}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (!frames.length) {
                  if (!anchorPose) return
                  onChange([
                    {
                      at: firstPinAt(),
                      lng: anchorPose.lng,
                      lat: anchorPose.lat,
                    },
                  ])
                  onFocusIndex(0)
                  return
                }
                const next = appendPoseWaypoint(frames, clock)
                onChange(next)
                onFocusIndex(Math.max(0, next.length - 1))
              }}
            >
              Add
            </button>
            <button
              type="button"
              className="sc-btn"
              disabled={!frames.length}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onChange(undefined)}
            >
              Clear
            </button>
            {frames.length >= 2 && onTogglePathPreview ? (
              <button
                type="button"
                className={`sc-btn${pathPreviewActive ? ' on' : ''}`}
                title="Play this object’s path at the Control Bar Preview rate — starts from the selected waypoint when one is selected"
                onClick={onTogglePathPreview}
              >
                {pathPreviewActive ? 'Stop' : 'Preview'}
              </button>
            ) : null}
            {frames.length >= 2 && showRetime ? (
              <button
                type="button"
                className={`sc-btn sc-series-csv-toggle${retimeOpen ? ' on' : ''}`}
                title="Rewrite middle times from the first pin using travel speed and an end time / intent"
                aria-expanded={retimeOpen}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (!retimeOpen) {
                    setIoOpen(false)
                    setRetimeIntent('leave-at-start')
                    seedRetimeDraft()
                  }
                  setRetimeOpen(!retimeOpen)
                }}
              >
                Retime
                <span className={`sc-series-csv-caret${retimeOpen ? ' up' : ''}`} aria-hidden="true" />
              </button>
            ) : null}
            <button
              type="button"
              className={`sc-btn sc-series-csv-toggle${ioOpen ? ' on' : ''}`}
              aria-expanded={ioOpen}
              onClick={() => {
                if (!ioOpen) {
                  setRetimeOpen(false)
                  setIoTab('import')
                  seedImportDraft()
                }
                setIoOpen(!ioOpen)
              }}
            >
              CSV
              <span className={`sc-series-csv-caret${ioOpen ? ' up' : ''}`} aria-hidden="true" />
            </button>
          </div>
          <div className={`sc-fold-body${showRetime && retimeOpen ? ' open' : ''}`}>
            <div className="sc-fold-inner">
              <div className="sc-series-io-body">
                <div className="sc-series-io-tabs" role="tablist" aria-label="Retime intent">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={retimeIntent === 'leave-at-start'}
                    className={`sc-series-io-tab${retimeIntent === 'leave-at-start' ? ' on' : ''}`}
                    onClick={() => setRetimeIntent('leave-at-start')}
                  >
                    Leave on time
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={retimeIntent === 'arrive-at-end'}
                    className={`sc-series-io-tab${retimeIntent === 'arrive-at-end' ? ' on' : ''}`}
                    onClick={() => setRetimeIntent('arrive-at-end')}
                  >
                    Arrive on time
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={retimeIntent === 'natural-only'}
                    className={`sc-series-io-tab${retimeIntent === 'natural-only' ? ' on' : ''}`}
                    onClick={() => setRetimeIntent('natural-only')}
                  >
                    Natural only
                  </button>
                </div>
                {retimeIntent === 'leave-at-start' ? (
                  <p className="sc-hint">
                    Leave at the start time; hold at the last pin if early. Midpoint times will be
                    written.
                  </p>
                ) : null}
                {retimeIntent === 'arrive-at-end' ? (
                  <>
                    <p className="sc-hint">
                      Hold at first pin, then travel so arrival matches end time. Midpoint times
                      will be written.
                    </p>
                    <label className="sc-field sc-field-tight">
                      <span className="sc-flbl">End time</span>
                      <input
                        type="text"
                        className="sc-fin"
                        value={retimeEndText}
                        placeholder={authorTimePlaceholder(clock)}
                        onChange={(e) => {
                          setRetimeEndText(e.target.value)
                          setRetimeBad(false)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') applyRetime()
                        }}
                      />
                    </label>
                  </>
                ) : null}
                {retimeIntent === 'natural-only' ? (
                  <p className="sc-hint">
                    Travel from the start time only at average mph; end time becomes natural arrival.
                  </p>
                ) : null}
                {retimeBad ? (
                  <p className="sc-hint" style={{ color: '#f0a0a0' }}>
                    Could not parse end time — use H:MM or H:MM:SS (at or after scenario start).
                  </p>
                ) : null}
                <div className="sc-series-actions sc-series-io-actions">
                  <button type="button" className="sc-btn" onClick={applyRetime}>
                    Update times
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className={`sc-fold-body${ioOpen ? ' open' : ''}`}>
            <div className="sc-fold-inner">
              <div className="sc-series-io-body">
                <div className="sc-series-io-tabs" role="tablist" aria-label="CSV mode">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={ioTab === 'import'}
                    className={`sc-series-io-tab${ioTab === 'import' ? ' on' : ''}`}
                    onClick={() => {
                      if (ioTab === 'import') return
                      setIoTab('import')
                      seedImportDraft()
                    }}
                  >
                    Import
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={ioTab === 'export'}
                    className={`sc-series-io-tab${ioTab === 'export' ? ' on' : ''}`}
                    onClick={() => {
                      if (ioTab === 'export') return
                      setIoTab('export')
                      setPasteBad(false)
                    }}
                  >
                    Export
                  </button>
                </div>
                <p className="sc-hint sc-series-paste-hint">{csvHint}</p>
                <textarea
                  className={`sc-fin sc-series-paste${pasteBad && ioTab === 'import' ? ' bad' : ''}`}
                  value={ioTab === 'export' ? exportText : csvDraft}
                  readOnly={ioTab === 'export'}
                  spellCheck={false}
                  aria-label={ioTab === 'import' ? 'Import waypoints CSV' : 'Export waypoints CSV'}
                  placeholder={`${POSE_CSV_HEADER}\n10:54:00, 33.8520800, -115.4765500`}
                  onChange={
                    ioTab === 'import'
                      ? (e) => {
                          setCsvDraft(e.target.value)
                          if (pasteBad) setPasteBad(parsePosePaste(e.target.value, playheadIso, clock) == null)
                        }
                      : undefined
                  }
                  onFocus={ioTab === 'export' ? (e) => e.currentTarget.select() : undefined}
                />
                {pasteBad && ioTab === 'import' ? (
                  <span className="sc-elapsed-err sc-series-paste-hint">
                    Could not parse CSV — use time, lat, lng.
                  </span>
                ) : null}
                <div className="sc-series-actions sc-series-io-actions">
                  {ioTab === 'import' ? (
                    <button type="button" className="sc-btn primary" onClick={applyImport}>
                      Apply import
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="sc-btn sc-btn-clipboard"
                      onClick={() => {
                        void (async () => {
                          try {
                            await navigator.clipboard.writeText(exportText)
                            flashIoNote('Copied')
                          } catch {
                            flashIoNote('Copy failed')
                          }
                        })()
                      }}
                    >
                      Copy to clipboard
                    </button>
                  )}
                  {ioNote ? (
                    <span className="sc-series-io-note" role="status">
                      {ioNote}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}

export function VisibilitySeriesEditor({
  frames,
  editing,
  clock,
  appearAt,
  playheadIso,
  focusIndex = null,
  previewIndex,
  visPreviewActive = false,
  onToggleVisPreview,
  onFocusIndex,
  onChange,
}: {
  frames: VisibilityKeyframe[]
  editing: boolean
  clock: ScenarioClockConfig
  appearAt?: string | null
  playheadIso: string
  focusIndex?: number | null
  previewIndex?: number | null
  visPreviewActive?: boolean
  onToggleVisPreview?: () => void
  onFocusIndex?: (index: number | null) => void
  onChange: (frames: VisibilityKeyframe[] | undefined) => void
}) {
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])
  const [flashMs, setFlashMs] = useState<string | null>(null)
  const flashTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (previewIndex == null) return
    const row = rowRefs.current[previewIndex]
    const body = row?.closest('.sc-fold-body')
    if (body && !body.classList.contains('open')) return
    row?.scrollIntoView({ block: 'nearest' })
  }, [previewIndex])

  useEffect(() => {
    return () => {
      if (flashTimerRef.current != null) window.clearTimeout(flashTimerRef.current)
    }
  }, [])

  function firstAt(): string {
    return appearAt ?? playheadIso
  }

  function flashTime(ms: string): void {
    if (flashTimerRef.current != null) window.clearTimeout(flashTimerRef.current)
    setFlashMs(ms)
    flashTimerRef.current = window.setTimeout(() => {
      setFlashMs(null)
      flashTimerRef.current = null
    }, 650)
  }

  function commitTime(index: number, ms: string): void {
    const unique = uniquePoseAt(ms, frames, index)
    const cur = frames[index]
    if (!cur) return
    const updated = frames.map((f, i) => (i === index ? { ...f, at: unique } : f))
    onChange(sortVisFrames(updated))
    flashTime(unique)
  }

  const showTableChrome = editing || frames.length > 0

  return (
    <div className="sc-series">
      {showTableChrome ? (
        <div className={`sc-series-head sc-series-head-vis-time${editing ? '' : ' read'}`}>
          <span className="sc-series-head-num">#</span>
          <span>Time</span>
          <span>Visibility</span>
          {editing ? <span className="sc-series-head-x" aria-hidden="true" /> : null}
        </div>
      ) : null}
      {frames.length
        ? frames.map((frame, index) => {
            // Mirror waypoints: Preview lights one row; otherwise focused row is hot.
            const rowTone =
              previewIndex != null
                ? previewIndex === index
                  ? ' preview'
                  : ''
                : editing && focusIndex === index
                  ? ' hot'
                  : ''
            return (
              <div
                key={`${frame.at}-${index}`}
                ref={(el) => {
                  rowRefs.current[index] = el
                }}
                className={`sc-series-row vis-time${editing ? '' : ' read'}${rowTone}`}
                onClick={() => {
                  if (!editing || previewIndex != null || !onFocusIndex) return
                  onFocusIndex(index)
                }}
              >
                <span className="sc-series-num" aria-hidden="true">
                  {index + 1}
                </span>
                {editing ? (
                  <>
                    <ElapsedInput
                      valueIso={frame.at}
                      clock={clock}
                      flash={flashMs === frame.at}
                      onFocusRow={() => {
                        if (previewIndex != null || !onFocusIndex) return
                        onFocusIndex(index)
                      }}
                      onCommit={(ms) => {
                        if (ms == null) return
                        commitTime(index, ms)
                      }}
                    />
                    <select
                      className="sc-fin sc-perm"
                      value={frame.visible ? 'show' : 'hide'}
                      aria-label="Visibility"
                      onChange={(e) => {
                        const visible = e.target.value === 'show'
                        onChange(frames.map((f, i) => (i === index ? { ...f, visible } : f)))
                      }}
                    >
                      <option value="show">Visible</option>
                      <option value="hide">Hidden</option>
                    </select>
                    <button
                      type="button"
                      className="sc-x"
                      title="Remove"
                      aria-label="Remove"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        const next = frames.filter((_, i) => i !== index)
                        onChange(next.length ? sortVisFrames(next) : undefined)
                      }}
                    >
                      ×
                    </button>
                  </>
                ) : (
                  <>
                    <span className="val sc-series-time-read">
                      {formatAuthorTime(frame.at, clock)}
                    </span>
                    <span className="val">{frame.visible ? 'Visible' : 'Hidden'}</span>
                  </>
                )}
              </div>
            )
          })
        : editing ? (
          <button
            type="button"
            className="sc-series-empty"
            title="Add the first visibility step at Show time or the current clock"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() =>
              onChange([
                {
                  at: firstAt(),
                  visible: true,
                },
              ])
            }
          >
            Click Add to set first visibility time
          </button>
        ) : null}
      {editing ? (
        <div className="sc-series-actions">
          <button
            type="button"
            className="sc-btn"
            title={
              frames.length
                ? `Add the next step (+${WAYPOINT_STEP_MS / 1000}s), toggling visible/hidden`
                : 'Add the first visibility step'
            }
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (!frames.length) {
                onChange([{ at: firstAt(), visible: true }])
                return
              }
              const sorted = sortVisFrames(frames)
              const last = sorted[sorted.length - 1]!
              const at = uniquePoseAt(
                addAuthorVisibleMs(last.at, WAYPOINT_STEP_MS, clock),
                sorted,
              )
              onChange(
                sortVisFrames([
                  ...sorted,
                  { at, visible: !last.visible },
                ]),
              )
            }}
          >
            Add
          </button>
          <button
            type="button"
            className="sc-btn"
            disabled={!frames.length}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange(undefined)}
          >
            Clear all
          </button>
          {frames.length >= 2 && onToggleVisPreview ? (
            <button
              type="button"
              className={`sc-btn${visPreviewActive ? ' on' : ''}`}
              title="Play visibility keyframes at the Control Bar Preview rate"
              onClick={onToggleVisPreview}
            >
              {visPreviewActive ? 'Stop' : 'Preview'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
